import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import type { PersistStorage } from 'zustand/middleware';
import type { GameState } from '@/src/store/gameStore';

const isNative = Capacitor.isNativePlatform();

// Retry utility for slow native plugin calls
const retry = async <T>(fn: () => Promise<T>, retries = 2, delay = 100): Promise<T> => {
  try {
    return await fn();
  } catch (err) {
    if (retries <= 0) throw err;
    console.warn('[secureStorageAdapter] retrying...', { retriesLeft: retries - 1 });
    await new Promise((res) => setTimeout(res, delay));
    return retry(fn, retries - 1, delay);
  }
};

export const secureStorageAdapter: PersistStorage<Partial<GameState>> = {
  getItem: async (key) => {
    console.log('[secureStorageAdapter] getItem START', key);
    try {
      if (isNative) {
        const { value } = await retry(() => SecureStoragePlugin.get({ key }));
        console.log('[secureStorageAdapter] getItem DONE (native)', value);
        return value ? JSON.parse(value) : null;
      } else {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      }
    } catch (e) {
      console.warn(`[secureStorageAdapter] getItem error for "${key}":`, e);
      return null;
    }
  },

  setItem: async (key, value) => {
    const serialized = JSON.stringify(value);
    try {
      if (isNative) {
        await SecureStoragePlugin.set({ key, value: serialized });
        console.log('[secureStorageAdapter] setItem DONE (native)', key);
      } else {
        localStorage.setItem(key, serialized);
      }
    } catch (e) {
      console.warn(`[secureStorageAdapter] setItem error for "${key}":`, e);
    }
  },

  removeItem: async (key) => {
    try {
      if (isNative) {
        await SecureStoragePlugin.remove({ key });
        console.log('[secureStorageAdapter] removeItem DONE (native)', key);
      } else {
        localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn(`[secureStorageAdapter] removeItem error for "${key}":`, e);
    }
  },
};
